/**
 * The Starfall Wyrm's HP (M40, docs/RARES.md; Kevin 2026-10-12: "The enemy should be scaled to the power of the user,
 * giving only around a 50% chance for success"). One formula, in one place (`wyrmHp`), read from the board as it stands
 * when the Wyrm appears (pure: the wave preview asks it too):
 *
 *   HP = max(factor × passDamage, floorMult × the HP a rare would have at this stage)
 *
 * - `factor`: `powerFactor` by the level's chapter on a campaign level; `ironPowerFactor` in Iron, `endlessPowerFactor`
 *   in Endless (calibrated each to a ~50 % kill by the strategic player: what a pass deals varies with how much else is on
 *   the road — Iron's blocks never pause — and with how a chapter's roads and raiders draw the towers' fire).
 *
 * - `passDamage` (`airPassDamage`): what the board can deal the Wyrm on one pass of the road — for every live tower
 *   that can hit air, its damage per second against it (`towerAirDps`) × the seconds the Wyrm spends in that tower's
 *   reach (the road cells within its range ÷ the Wyrm's speed), summed, × the best Hex curse that reaches air.
 *   A tower's damage per second: its damage now (tier, level, gear, aura) × TicksPerSecond ÷ its fire interval now, for
 *   towers whose type shoots air, the fortress (its own tower), a tier-4 Mortar with Seeker Shells and a tier-4 Rangers'
 *   Camp (its living soldiers each loose an arrow); the Arcane Spire at its full ramp (a beam held on one long-lived
 *   target); an area tower's hit × `areaVsHeavyMult` (the Wyrm is a boss, so heavy), a physical one × (1 − its armour).
 *   Beacons count through the towers they boost; Frost (no damage) and burns (no burning tower shoots air) add nothing.
 *   (M40 first tried the whole road's seconds × the board's air dps, `airDps × wyrmExposure`; the strategic player's
 *   kill rate then ran 25 % on L21–30 against 81 % on L5–10 at one factor, as boards cover their roads unevenly. Each
 *   tower's own reach halved the spread: docs/RARES.md.)
 * - the floor: a board with no anti-air still meets a real beast, one the rare rally's three free hands can answer.
 *
 * The factors and `floorMult` (enemies.json wyrmRules) are calibrated to a ~50 % kill chance (docs/RARES.md).
 */
import { DamageType, isAura, isBarracks, isBeam, isHex } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { distanceSquared } from "../world/roadGeometry";
import { SimConstants } from "./constants";
import { Tower } from "./entities";
import type { LevelSim } from "./levelSim";
import { BASE_TIERS, branchOf } from "./branches/ascension";
import { rocketHitsAir } from "./branches/shells";
import { beamFullMultiplier } from "./systems/beam";
import { hexBonusOf } from "./systems/hex";
import { chapterOfLevel, rareHpMult } from "./rares";

/** Below this the Wyrm's speed counts as this (cells a second), so no division blows up on odd data. */
const MIN_SPEED = Fix64.fromFraction(1, 100);

/** The Wyrm's HP were it to appear on stage `stage` now (the formula above). */
export function wyrmHp(sim: LevelSim, stage: number, wyrm: number): Fix64 {
  const W = sim.balance.spawnRules.wyrm;
  const scaled = airPassDamage(sim, wyrm).mul(wyrmPowerFactor(sim));
  // the HP a rare would have here: rareHpMult is a rare's HP over the archetype's, so × the Wyrm's own HP gives it
  const floor = rareHpMult(sim, stage, wyrm).mul(sim.balance.enemies[wyrm].hp).mul(W.floorMult);
  return Fix64.max(Fix64.max(scaled, floor), Fix64.One);
}

/** The run's `factor`: Iron's, Endless's, or the campaign level's chapter's (past the last chapter, the last). */
export function wyrmPowerFactor(sim: LevelSim): Fix64 {
  const W = sim.balance.spawnRules.wyrm;
  if (sim.isIron) return W.ironPowerFactor;
  if (sim.isEndless) return W.endlessPowerFactor;
  return W.powerFactor[Math.min(chapterOfLevel(sim.levelNumber), W.powerFactor.length) - 1];
}

/** What the board can deal the Wyrm on one pass: each air tower's dps × its seconds in reach, × the best Hex curse. */
export function airPassDamage(sim: LevelSim, wyrm: number): Fix64 {
  const cells = sim.road.roadCells, speed = Fix64.max(sim.balance.enemies[wyrm].speedCellsPerSecond, MIN_SPEED);
  return eachAirTower(sim, wyrm, (t, dps) => {
    const a = sim.balance.towers[t.archetype];
    const r = sim.rangeOf(t), r2 = r.mul(r), min2 = a.minRange.mul(a.minRange), tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
    let near = 0;
    for (const c of cells) { const d2 = distanceSquared(tx, ty, Fix64.fromInt(c.x), Fix64.fromInt(c.y)); if (d2.le(r2) && !d2.lt(min2)) near++; }
    return dps.mulInt(near).div(speed);
  });
}

/** The board's damage per second against ONE air target of archetype `wyrm`, wherever it is (the report's measure). */
export function airDps(sim: LevelSim, wyrm: number): Fix64 { return eachAirTower(sim, wyrm, (_t, dps) => dps); }

/** Seconds the Wyrm spends on the whole road: its length in cells ÷ its speed. */
export function wyrmExposure(sim: LevelSim, wyrm: number): Fix64 {
  return sim.road.length.div(Fix64.max(sim.balance.enemies[wyrm].speedCellsPerSecond, MIN_SPEED));
}

/** Σ over the live towers that hurt air of `part(tower, its dps)`, × (1 + the best curse a Hex Totem that reaches air lays). */
function eachAirTower(sim: LevelSim, wyrm: number, part: (t: Tower, dps: Fix64) => Fix64): Fix64 {
  let total = Fix64.Zero, curse = Fix64.Zero;
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    const a = sim.balance.towers[t.archetype];
    if (isHex(a)) { if (a.targetsAir) curse = Fix64.max(curse, hexBonusOf(sim, t)); continue; }
    if (isAura(a)) continue;
    const dps = towerAirDps(sim, t, wyrm);
    if (!dps.isZero()) total = total.add(part(t, dps));
  }
  return curse.isZero() ? total : total.mul(Fix64.One.add(curse));
}

/** One tower's damage per second against the Wyrm (zero if it cannot hit air). */
function towerAirDps(sim: LevelSim, t: Tower, wyrm: number): Fix64 {
  const B = sim.balance, a = B.towers[t.archetype], tier4 = t.tier > BASE_TIERS;
  let shots = 1;
  if (isBarracks(a)) {
    // Barracks fight through soldiers, and only a Rangers' Camp's bows reach the air (sim/barracks.ts)
    if (!tier4 || (branchOf(sim, t)?.tier4.int("hitsAir") ?? 0) <= 0) return Fix64.Zero;
    shots = sim.squads.find((q) => q.towerId === t.id)?.members.filter((s) => s.alive).length ?? 0;
  } else if (!a.targetsAir && !(tier4 && rocketHitsAir(sim, t))) return Fix64.Zero;
  const interval = sim.fireIntervalOf(t);
  if (shots <= 0 || interval <= 0) return Fix64.Zero;
  let hit = sim.damageOf(t);
  if (isBeam(a)) hit = hit.mul(beamFullMultiplier(sim, t));
  if (a.area) hit = hit.mul(B.economy.areaVsHeavyMult);
  if (a.damageType === DamageType.Physical) hit = hit.mul(Fix64.One.sub(Fix64.clamp(B.enemies[wyrm].armour, Fix64.Zero, Fix64.One)));
  return hit.mulInt(shots * SimConstants.TicksPerSecond).divInt(interval);
}
